import React, { useState } from 'react';
import { useProdutos } from '../../hooks/useProdutos';
import { useTiposPagamento } from '../../hooks/useTiposPagamento';
import {
  useRegistrarPedido,
  useListarMesasAbertas,
  useAdicionarItemPedido,
  useRemoverItemPedido,
  useFecharMesa,
  useAtualizarDescontoPedido,
  useCancelarPedido,
} from '../../hooks/usePedidos';
import { usePedidosDoDia, useStatusPedidos, useAlterarStatusPedido } from '../../hooks/useStatusPedidos';
import { useObterEstabelecimento } from '../../hooks/useAdmin';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import Button from '../../components/Common/Button';
import ConfirmModal from '../../components/Common/ConfirmModal';
import { MesaAbertaDto, ItemPedido } from '../../types';

interface ItemCarrinho {
  produtoId: number;
  produtoNome: string;
  precoUnitario: number;
  quantidade: number;
}

const PDV: React.FC = () => {
  usePageTitle('PDV');
  const { user } = useAuth();
  const { data: produtos, isLoading: loadingProdutos } = useProdutos(true);
  const { data: tiposPagamento, isLoading: loadingTipos } = useTiposPagamento();
  const { mutate: registrarPedido, isPending } = useRegistrarPedido();
  const { data: statusPedidos, isLoading: loadingStatus } = useStatusPedidos();
  const { mutate: alterarStatus, isPending: alterandoStatus } = useAlterarStatusPedido();
  const { data: estabelecimento } = useObterEstabelecimento(user?.estabelecimentoId);
  const { data: mesasAbertas, isLoading: loadingMesas } = useListarMesasAbertas();
  const { mutate: adicionarItem, isPending: adicionandoItem } = useAdicionarItemPedido();
  const { mutate: removerItem } = useRemoverItemPedido();
  const { mutate: fecharMesa, isPending: fechandoMesa } = useFecharMesa();
  const { mutate: atualizarDesconto, isPending: atualizandoDesconto } = useAtualizarDescontoPedido();
  const { mutate: cancelarPedido, isPending: cancelandoPedido } = useCancelarPedido();

  const quantidadeMesas = estabelecimento?.quantidadeMesas ?? 0;

  const [carrinho, setCarrinho] = useState<ItemCarrinho[]>([]);
  const [tipoPagamentoId, setTipoPagamentoId] = useState<number | null>(null);
  const [desconto, setDesconto] = useState('');
  const [pago, setPago] = useState(false);
  const [numeroMesa, setNumeroMesa] = useState<number | ''>('');
  const [vendaRetroativa, setVendaRetroativa] = useState(false);
  const [dataHoraRetroativa, setDataHoraRetroativa] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [pedidoExpandido, setPedidoExpandido] = useState<number | null>(null);
  const tamanhoPagina = 10;

  // Modal adicionar itens a mesa aberta
  const [modalAdicionarItens, setModalAdicionarItens] = useState<{
    mesaId: number | null;
    aberto: boolean;
    quantidades: Record<number, number>;
  }>({ mesaId: null, aberto: false, quantidades: {} });

  // Modal fechar mesa
  const [modalFecharMesa, setModalFecharMesa] = useState<{
    mesa: MesaAbertaDto | null;
    tipoPagamentoId: number | null;
  }>({ mesa: null, tipoPagamentoId: null });

  // Modal editar desconto
  const [modalEditarDesconto, setModalEditarDesconto] = useState<{
    pedidoId: number | null;
    descontoAtual: string;
  }>({ pedidoId: null, descontoAtual: '' });

  // Modal cancelar pedido
  const [modalCancelarPedido, setModalCancelarPedido] = useState<{
    isOpen: boolean;
    pedidoId: number | null;
    numeroMesa: number | null;
  }>({
    isOpen: false,
    pedidoId: null,
    numeroMesa: null,
  });

  // Modal confirmar remoção de item
  const [modalRemoverItem, setModalRemoverItem] = useState<{
    isOpen: boolean;
    pedidoId: number | null;
    itemId: number | null;
    itemNome: string;
  }>({
    isOpen: false,
    pedidoId: null,
    itemId: null,
    itemNome: '',
  });

  // Modal confirmar alteração de status
  const [confirmarModal, setConfirmarModal] = useState<{
    isOpen: boolean;
    pedidoId: number | null;
    novoStatusId: number | null;
    statusNome: string;
  }>({
    isOpen: false,
    pedidoId: null,
    novoStatusId: null,
    statusNome: '',
  });

  const { data: dadosPedidos, isLoading: loadingPedidos } = usePedidosDoDia(paginaAtual, tamanhoPagina);

  const adicionarAoCarrinho = (produtoId: number, nome: string, preco: number) => {
    setCarrinho((prev) => {
      const itemExistente = prev.find((item) => item.produtoId === produtoId);
      if (itemExistente) {
        return prev.map((item) =>
          item.produtoId === produtoId ? { ...item, quantidade: item.quantidade + 1 } : item
        );
      }
      return [...prev, { produtoId, produtoNome: nome, precoUnitario: preco, quantidade: 1 }];
    });
  };

  const removerDoCarrinho = (produtoId: number) => {
    setCarrinho((prev) => prev.filter((item) => item.produtoId !== produtoId));
  };

  const atualizarQuantidade = (produtoId: number, quantidade: number) => {
    if (quantidade <= 0) {
      removerDoCarrinho(produtoId);
      return;
    }
    setCarrinho((prev) =>
      prev.map((item) => (item.produtoId === produtoId ? { ...item, quantidade } : item))
    );
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getStatusBadgeClass = (statusDescricao: string) => {
    const status = statusDescricao.toLowerCase();

    if (status.includes('aguardando') || status.includes('pendente')) {
      return 'bg-yellow-100 text-yellow-800';
    }
    if (status.includes('pago') || status.includes('concluido') || status.includes('conclu')) {
      return 'bg-green-100 text-green-800';
    }
    if (status.includes('cancelado')) {
      return 'bg-red-100 text-red-800';
    }
    if (status.includes('estornado')) {
      return 'bg-gray-100 text-gray-800';
    }
    return 'bg-blue-100 text-blue-800';
  };

  const agruparItens = (itens: ItemPedido[]) => {
    const mapa = new Map<number, ItemPedido & { valorTotal: number }>();
    for (const item of itens) {
      if (mapa.has(item.produtoId)) {
        const g = mapa.get(item.produtoId)!;
        g.quantidade += item.quantidade;
        g.valorTotal += item.valorTotal;
      } else {
        mapa.set(item.produtoId, { ...item });
      }
    }
    return Array.from(mapa.values()).sort((a, b) => a.nomeProduto.localeCompare(b.nomeProduto, 'pt-BR'));
  };

  const subtotal = carrinho.reduce((sum, item) => sum + item.precoUnitario * item.quantidade, 0);
  const descontoNum = parseFloat(desconto.replace(',', '.')) || 0;
  const total = subtotal - descontoNum;

  const finalizarVenda = () => {
    if (pago && !tipoPagamentoId) {
      alert('Selecione um tipo de pagamento');
      return;
    }

    if (carrinho.length === 0) {
      alert('Adicione produtos ao carrinho');
      return;
    }

    if (vendaRetroativa && !dataHoraRetroativa) {
      alert('Informe a data e hora da venda retroativa');
      return;
    }

    registrarPedido(
      {
        tipoPagamentoId: pago ? tipoPagamentoId! : undefined,
        desconto: descontoNum,
        pago,
        numeroMesa: numeroMesa !== '' ? numeroMesa : undefined,
        itens: carrinho.map((item) => ({
          produtoId: item.produtoId,
          quantidade: item.quantidade,
        })),
        ...(vendaRetroativa && dataHoraRetroativa
          ? { dataHora: dataHoraRetroativa }
          : {}),
      },
      {
        onSuccess: () => {
          setCarrinho([]);
          setDesconto('');
          setTipoPagamentoId(null);
          setPago(false);
          setNumeroMesa('');
          setVendaRetroativa(false);
          setDataHoraRetroativa('');
        },
      }
    );
  };

  const handleAlterarStatus = (pedidoId: number, novoStatusId: number) => {
    const statusSelecionado = statusPedidos?.find(s => s.id === novoStatusId);
    const statusNome = statusSelecionado?.descricao || 'novo status';

    setConfirmarModal({
      isOpen: true,
      pedidoId,
      novoStatusId,
      statusNome,
    });
  };

  const confirmarAlteracaoStatus = () => {
    if (confirmarModal.pedidoId && confirmarModal.novoStatusId) {
      alterarStatus({
        pedidoId: confirmarModal.pedidoId,
        novoStatusId: confirmarModal.novoStatusId
      });
    }
    setConfirmarModal({
      isOpen: false,
      pedidoId: null,
      novoStatusId: null,
      statusNome: '',
    });
  };

  const cancelarAlteracaoStatus = () => {
    setConfirmarModal({
      isOpen: false,
      pedidoId: null,
      novoStatusId: null,
      statusNome: '',
    });
  };

  const handleConfirmarAdicionarItens = () => {
    if (!modalAdicionarItens.mesaId) return;
    const itens = Object.entries(modalAdicionarItens.quantidades).filter(([, q]) => q > 0);
    if (itens.length === 0) {
      alert('Selecione pelo menos um produto');
      return;
    }
    const mesaId = modalAdicionarItens.mesaId;
    setModalAdicionarItens({ mesaId: null, aberto: false, quantidades: {} });
    itens.forEach(([produtoId, quantidade]) => {
      adicionarItem({ pedidoId: mesaId, request: { produtoId: parseInt(produtoId), quantidade } });
    });
  };

  const handleConfirmarFecharMesa = () => {
    if (!modalFecharMesa.mesa || !modalFecharMesa.tipoPagamentoId) {
      alert('Selecione o tipo de pagamento');
      return;
    }
    fecharMesa(
      { pedidoId: modalFecharMesa.mesa.id, request: { tipoPagamentoId: modalFecharMesa.tipoPagamentoId } },
      { onSuccess: () => setModalFecharMesa({ mesa: null, tipoPagamentoId: null }) }
    );
  };

  const handleConfirmarEditarDesconto = () => {
    if (modalEditarDesconto.pedidoId === null) return;
    const descontoNum = parseFloat(modalEditarDesconto.descontoAtual.replace(',', '.')) || 0;
    atualizarDesconto(
      { pedidoId: modalEditarDesconto.pedidoId, desconto: descontoNum },
      { onSuccess: () => setModalEditarDesconto({ pedidoId: null, descontoAtual: '' }) }
    );
  };

  const handleConfirmarCancelarPedido = () => {
    if (modalCancelarPedido.pedidoId === null) return;
    cancelarPedido(modalCancelarPedido.pedidoId, {
      onSuccess: () => setModalCancelarPedido({ isOpen: false, pedidoId: null, numeroMesa: null }),
    });
  };

  const handleConfirmarRemoverItem = () => {
    if (modalRemoverItem.pedidoId === null || modalRemoverItem.itemId === null) return;
    removerItem(
      { pedidoId: modalRemoverItem.pedidoId, itemId: modalRemoverItem.itemId },
      { onSuccess: () => setModalRemoverItem({ isOpen: false, pedidoId: null, itemId: null, itemNome: '' }) }
    );
  };

  return (
    <div>
      <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">PDV - Ponto de Venda</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Produtos</h2>
          {loadingProdutos ? (
            <p className="text-gray-500">Carregando produtos...</p>
          ) : produtos && produtos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {produtos.filter((produto) => !produto.sobEncomenda).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')).map((produto) => (
                <div
                  key={produto.id}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => adicionarAoCarrinho(produto.id, produto.nome, produto.precoUnitario)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-800">{produto.nome}</h3>
                      {produto.descricao && (
                        <p className="text-sm text-gray-500">{produto.descricao}</p>
                      )}
                    </div>
                    <p className="text-lg font-bold text-blue-600">
                      {formatCurrency(produto.precoUnitario)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Nenhum produto disponível</p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Carrinho</h2>
          {carrinho.length > 0 ? (
            <div className="space-y-4">
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {carrinho.map((item) => (
                  <div key={item.produtoId} className="flex items-center justify-between pb-3 border-b">
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{item.produtoNome}</p>
                      <p className="text-sm text-gray-500">{formatCurrency(item.precoUnitario)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => atualizarQuantidade(item.produtoId, item.quantidade - 1)}
                        className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded hover:bg-gray-300"
                      >
                        -
                      </button>
                      <span className="w-8 text-center font-semibold">{item.quantidade}</span>
                      <button
                        onClick={() => atualizarQuantidade(item.produtoId, item.quantidade + 1)}
                        className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded hover:bg-gray-300"
                      >
                        +
                      </button>
                      <button
                        onClick={() => removerDoCarrinho(item.produtoId)}
                        className="ml-2 text-red-600 hover:text-red-800"
                      >
                        X
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-2 pt-4 border-t">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-semibold">{formatCurrency(subtotal)}</span>
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Desconto:</label>
                  <input
                    type="text"
                    value={desconto}
                    onChange={(e) => setDesconto(e.target.value.replace(/[^0-9.,]/g, ''))}
                    className="flex-1 px-2 py-1 border rounded text-sm"
                    placeholder="0.00"
                  />
                </div>

                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                  <span>Total:</span>
                  <span className="text-blue-600">{formatCurrency(total)}</span>
                </div>
              </div>

              {quantidadeMesas > 0 && (
                <div className="space-y-1 pt-2">
                  <label className="block text-sm font-medium text-gray-700">Mesa</label>
                  <select
                    value={numeroMesa}
                    onChange={(e) => setNumeroMesa(e.target.value === '' ? '' : parseInt(e.target.value))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sem mesa</option>
                    {Array.from({ length: quantidadeMesas }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>Mesa {n}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="pago"
                  checked={pago}
                  onChange={(e) => {
                    setPago(e.target.checked);
                    if (!e.target.checked) setTipoPagamentoId(null);
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="pago" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Pedido pago
                </label>
              </div>

              {pago && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Tipo de Pagamento
                  </label>
                  {loadingTipos ? (
                    <p className="text-sm text-gray-500">Carregando...</p>
                  ) : (
                    <select
                      value={tipoPagamentoId || ''}
                      onChange={(e) => setTipoPagamentoId(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione...</option>
                      {tiposPagamento?.map((tipo) => (
                        <option key={tipo.id} value={tipo.id}>
                          {tipo.descricao}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="retroativa"
                  checked={vendaRetroativa}
                  onChange={(e) => {
                    setVendaRetroativa(e.target.checked);
                    if (!e.target.checked) setDataHoraRetroativa('');
                  }}
                  className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-400"
                />
                <label htmlFor="retroativa" className="text-sm font-medium text-gray-500 cursor-pointer">
                  Venda retroativa
                </label>
              </div>

              {vendaRetroativa && (
                <div className="mt-1">
                  <label className="block text-xs text-gray-500 mb-1">Data e hora da venda</label>
                  <input
                    type="date"
                    value={dataHoraRetroativa}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setDataHoraRetroativa(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Button
                  onClick={finalizarVenda}
                  disabled={isPending}
                  variant="primary"
                  size="lg"
                  fullWidth
                >
                  {isPending ? 'Finalizando...' : pago ? 'Finalizar Venda' : 'Abrir Mesa'}
                </Button>
                <Button
                  onClick={() => {
                    setCarrinho([]);
                    setDesconto('');
                    setTipoPagamentoId(null);
                    setNumeroMesa('');
                  }}
                  variant="secondary"
                  size="md"
                  fullWidth
                >
                  Limpar Carrinho
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Carrinho vazio</p>
          )}
        </div>
      </div>

      {/* Mesas Abertas */}
      <div className="mt-8 bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Mesas Abertas</h2>

        {loadingMesas ? (
          <p className="text-gray-500 text-center py-4">Carregando mesas...</p>
        ) : mesasAbertas && mesasAbertas.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {mesasAbertas.map((mesa) => (
              <div key={mesa.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800">
                      {mesa.numeroMesa ? `Mesa ${mesa.numeroMesa}` : 'Sem mesa'} &nbsp;
                      <span className="text-xs text-gray-500 font-normal">#{mesa.id}</span>
                    </h3>
                    <span className="text-xs text-gray-500">{formatDateTime(mesa.dataHora)}</span>
                  </div>
                  <button
                    onClick={() => setModalCancelarPedido({ isOpen: true, pedidoId: mesa.id, numeroMesa: mesa.numeroMesa ?? null })}
                    className="bg-red-500 hover:bg-red-600 text-white font-bold rounded w-7 h-7 flex items-center justify-center transition-colors shadow-sm hover:shadow-md"
                    title="Cancelar pedido"
                  >
                    ×
                  </button>
                </div>

                <div className="space-y-1">
                  {agruparItens(mesa.itens).map((item) => (
                    <div key={item.produtoId} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{item.quantidade}x {item.nomeProduto}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">{formatCurrency(item.valorTotal)}</span>
                        <button
                          onClick={() => setModalRemoverItem({
                            isOpen: true,
                            pedidoId: mesa.id,
                            itemId: item.id,
                            itemNome: item.nomeProduto
                          })}
                          className="text-red-500 hover:text-red-700 text-xs font-bold"
                          title="Remover item"
                        >
                          x
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t pt-2 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal:</span>
                    <span>{formatCurrency(mesa.valorSubtotal)}</span>
                  </div>
                  {mesa.desconto > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Desconto:</span>
                      <span className="text-red-600">-{formatCurrency(mesa.desconto)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Total:</span>
                    <span className="text-blue-600">{formatCurrency(mesa.valorTotal)}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => setModalAdicionarItens({ mesaId: mesa.id, aberto: true, quantidades: {} })}
                    variant="secondary"
                    size="sm"
                    fullWidth
                  >
                    + Adicionar itens
                  </Button>
                  <Button
                    onClick={() => setModalEditarDesconto({ pedidoId: mesa.id, descontoAtual: String(mesa.desconto || '') })}
                    variant="secondary"
                    size="sm"
                    fullWidth
                  >
                    Editar desconto
                  </Button>
                  <Button
                    onClick={() => setModalFecharMesa({ mesa, tipoPagamentoId: null })}
                    variant="primary"
                    size="sm"
                    fullWidth
                  >
                    Fechar mesa
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">Nenhuma mesa aberta no momento</p>
        )}
      </div>

      {/* Gestão de Pedidos do Dia */}
      <div className="mt-8 bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Gestão de Pedidos do Dia</h2>

        {loadingPedidos || loadingStatus ? (
          <p className="text-gray-500 text-center py-8">Carregando pedidos...</p>
        ) : dadosPedidos && dadosPedidos.pedidos && dadosPedidos.pedidos.length > 0 ? (
          <div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data/Hora
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mesa
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Alterar Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Detalhes
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dadosPedidos.pedidos.map((pedido) => (
                    <React.Fragment key={pedido.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">#{pedido.id}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatDateTime(pedido.dataHora)}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {pedido.numeroMesa ? `Mesa ${pedido.numeroMesa}` : '-'}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {formatCurrency(pedido.valorTotal)}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(
                              pedido.statusDescricao
                            )}`}
                          >
                            {pedido.statusDescricao}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <select
                            value={pedido.statusPedidoId}
                            onChange={(e) =>
                              handleAlterarStatus(pedido.id, parseInt(e.target.value))
                            }
                            disabled={alterandoStatus}
                            className="px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                          >
                            {statusPedidos?.map((status) => (
                              <option key={status.id} value={status.id}>
                                {status.descricao}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            onClick={() =>
                              setPedidoExpandido(pedidoExpandido === pedido.id ? null : pedido.id)
                            }
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                          >
                            {pedidoExpandido === pedido.id ? 'Ocultar' : 'Ver'}
                          </button>
                        </td>
                      </tr>
                      {pedidoExpandido === pedido.id && (
                        <tr>
                          <td colSpan={7} className="px-4 py-4 bg-gray-50">
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-sm font-medium text-gray-700">Subtotal:</p>
                                  <p className="text-sm text-gray-900">
                                    {formatCurrency(pedido.valorSubtotal)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-700">Desconto:</p>
                                  <p className="text-sm text-gray-900">
                                    {formatCurrency(pedido.desconto)}
                                  </p>
                                </div>
                                {pedido.valorEstornado > 0 && (
                                  <div>
                                    <p className="text-sm font-medium text-gray-700">Estornado:</p>
                                    <p className="text-sm text-red-600">
                                      {formatCurrency(pedido.valorEstornado)}
                                    </p>
                                  </div>
                                )}
                              </div>

                              <div>
                                <p className="text-sm font-medium text-gray-700 mb-2">Itens:</p>
                                <div className="bg-white rounded border">
                                  <table className="min-w-full">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">
                                          Produto
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">
                                          Qtd
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">
                                          Preço Unit.
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">
                                          Total
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                      {agruparItens(pedido.itens ?? []).map((item) => (
                                        <tr key={item.produtoId}>
                                          <td className="px-3 py-2 text-sm text-gray-900">
                                            {item.nomeProduto}
                                          </td>
                                          <td className="px-3 py-2 text-sm text-gray-900">
                                            {item.quantidade}
                                          </td>
                                          <td className="px-3 py-2 text-sm text-gray-900">
                                            {formatCurrency(item.precoUnitario)}
                                          </td>
                                          <td className="px-3 py-2 text-sm font-semibold text-gray-900">
                                            {formatCurrency(item.valorTotal)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-between border-t pt-4">
              <div className="text-sm text-gray-700">
                Mostrando {(paginaAtual - 1) * tamanhoPagina + 1} a{' '}
                {Math.min(paginaAtual * tamanhoPagina, dadosPedidos.totalCount)} de{' '}
                {dadosPedidos.totalCount} pedidos
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setPaginaAtual((prev) => Math.max(1, prev - 1))}
                  disabled={paginaAtual === 1}
                  variant="secondary"
                  size="sm"
                >
                  Anterior
                </Button>
                <div className="flex items-center px-3 py-1 text-sm text-gray-700">
                  Página {paginaAtual} de {dadosPedidos.totalPaginas}
                </div>
                <Button
                  onClick={() => setPaginaAtual((prev) => Math.min(dadosPedidos.totalPaginas, prev + 1))}
                  disabled={paginaAtual >= dadosPedidos.totalPaginas}
                  variant="secondary"
                  size="sm"
                >
                  Próxima
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">Nenhum pedido encontrado hoje</p>
        )}
      </div>

      {/* Modal Adicionar Itens */}
      {modalAdicionarItens.aberto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-4 md:p-6 w-full max-w-lg max-h-[90vh] flex flex-col">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Adicionar Itens</h2>
            <div className="flex-1 overflow-y-auto space-y-2">
              {produtos?.filter((produto) => !produto.sobEncomenda).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')).map((produto) => (
                <div key={produto.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium text-gray-800">{produto.nome}</p>
                    <p className="text-sm text-gray-500">{formatCurrency(produto.precoUnitario)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setModalAdicionarItens(prev => ({
                        ...prev,
                        quantidades: {
                          ...prev.quantidades,
                          [produto.id]: Math.max(0, (prev.quantidades[produto.id] || 0) - 1)
                        }
                      }))}
                      className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded hover:bg-gray-300"
                    >
                      -
                    </button>
                    <span className="w-8 text-center font-semibold">
                      {modalAdicionarItens.quantidades[produto.id] || 0}
                    </span>
                    <button
                      onClick={() => setModalAdicionarItens(prev => ({
                        ...prev,
                        quantidades: {
                          ...prev.quantidades,
                          [produto.id]: (prev.quantidades[produto.id] || 0) + 1
                        }
                      }))}
                      className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded hover:bg-gray-300"
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-4 pt-4 border-t">
              <Button onClick={handleConfirmarAdicionarItens} variant="primary" size="md" fullWidth disabled={adicionandoItem}>
                {adicionandoItem ? 'Adicionando...' : 'Adicionar'}
              </Button>
              <Button
                onClick={() => setModalAdicionarItens({ mesaId: null, aberto: false, quantidades: {} })}
                variant="secondary"
                size="md"
                fullWidth
                disabled={adicionandoItem}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Fechar Mesa */}
      {modalFecharMesa.mesa && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Fechar {modalFecharMesa.mesa.numeroMesa ? `Mesa ${modalFecharMesa.mesa.numeroMesa}` : 'Pedido'}
            </h2>

            <div className="space-y-2 mb-4">
              {agruparItens(modalFecharMesa.mesa.itens).map((item) => (
                <div key={item.produtoId} className="flex justify-between text-sm">
                  <span>{item.quantidade}x {item.nomeProduto}</span>
                  <span className="font-medium">{formatCurrency(item.valorTotal)}</span>
                </div>
              ))}
            </div>

            <div className="border-t pt-3 space-y-1 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span>{formatCurrency(modalFecharMesa.mesa.valorSubtotal)}</span>
              </div>
              {modalFecharMesa.mesa.desconto > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Desconto:</span>
                  <span className="text-red-600">-{formatCurrency(modalFecharMesa.mesa.desconto)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg">
                <span>Total:</span>
                <span className="text-blue-600">{formatCurrency(modalFecharMesa.mesa.valorTotal)}</span>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Pagamento</label>
              {loadingTipos ? (
                <p className="text-sm text-gray-500">Carregando...</p>
              ) : (
                <select
                  value={modalFecharMesa.tipoPagamentoId || ''}
                  onChange={(e) => setModalFecharMesa(prev => ({ ...prev, tipoPagamentoId: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione...</option>
                  {tiposPagamento?.map((tipo) => (
                    <option key={tipo.id} value={tipo.id}>{tipo.descricao}</option>
                  ))}
                </select>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleConfirmarFecharMesa}
                variant="primary"
                size="md"
                fullWidth
                disabled={fechandoMesa || !modalFecharMesa.tipoPagamentoId}
              >
                {fechandoMesa ? 'Fechando...' : 'Confirmar'}
              </Button>
              <Button
                onClick={() => setModalFecharMesa({ mesa: null, tipoPagamentoId: null })}
                variant="secondary"
                size="md"
                fullWidth
                disabled={fechandoMesa}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Desconto */}
      {modalEditarDesconto.pedidoId !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Editar Desconto</h2>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Desconto (R$)</label>
              <input
                type="text"
                value={modalEditarDesconto.descontoAtual}
                onChange={(e) => setModalEditarDesconto(prev => ({
                  ...prev,
                  descontoAtual: e.target.value.replace(/[^0-9.,]/g, '')
                }))}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0.00"
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleConfirmarEditarDesconto}
                variant="primary"
                size="md"
                fullWidth
                disabled={atualizandoDesconto}
              >
                {atualizandoDesconto ? 'Atualizando...' : 'Confirmar'}
              </Button>
              <Button
                onClick={() => setModalEditarDesconto({ pedidoId: null, descontoAtual: '' })}
                variant="secondary"
                size="md"
                fullWidth
                disabled={atualizandoDesconto}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cancelar Pedido */}
      <ConfirmModal
        isOpen={modalCancelarPedido.isOpen}
        title="Cancelar Pedido"
        message={`Tem certeza que deseja cancelar ${modalCancelarPedido.numeroMesa ? `a Mesa ${modalCancelarPedido.numeroMesa}` : 'este pedido'}? Esta ação não pode ser desfeita.`}
        confirmText="Sim, cancelar"
        cancelText="Não"
        onConfirm={handleConfirmarCancelarPedido}
        onCancel={() => setModalCancelarPedido({ isOpen: false, pedidoId: null, numeroMesa: null })}
      />

      {/* Modal Remover Item */}
      <ConfirmModal
        isOpen={modalRemoverItem.isOpen}
        title="Remover Item"
        message={`Tem certeza que deseja remover "${modalRemoverItem.itemNome}" do pedido?`}
        confirmText="Sim, remover"
        cancelText="Cancelar"
        onConfirm={handleConfirmarRemoverItem}
        onCancel={() => setModalRemoverItem({ isOpen: false, pedidoId: null, itemId: null, itemNome: '' })}
      />

      <ConfirmModal
        isOpen={confirmarModal.isOpen}
        title="Confirmar Alteração de Status"
        message={`Deseja alterar o status do pedido #${confirmarModal.pedidoId} para "${confirmarModal.statusNome}"?`}
        confirmText="Confirmar"
        cancelText="Cancelar"
        onConfirm={confirmarAlteracaoStatus}
        onCancel={cancelarAlteracaoStatus}
      />
    </div>
  );
};

export default PDV;
